import { toast } from 'sonner';
import { usePdInventoryStore } from '@/store/pd-inventory.store';
import { useTenantStore } from '@/store/tenant.store';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class PdInventoryIntegration {
    constructor(service) {
        this.service = service;
    }

    /**
     * Get error message from error object or fallback
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    async fetchPdInventoryList(params = {}) {
        const store = usePdInventoryStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (!tenantId) return;

        store.setLoading(true);
        store.setError(null);

        try {
            const currentPagination = store.pagination;
            const queryParams = {
                page: params.page || currentPagination.currentPage,
                pageSize: params.pageSize || currentPagination.itemsPerPage,
                search: params.search !== undefined ? params.search : store.search,
                sortBy: params.sortBy || store.sorting.sortBy,
                sortOrder: params.sortDirection || store.sorting.sortDirection,
                ...params.columnFilters,
                tenantId
            };

            const response = await this.service.getAll(queryParams);

            store.setData(response.data || []);
            store.setPagination({
                currentPage: parseInt(response.meta.page),
                itemsPerPage: parseInt(response.meta.limit),
                totalItems: parseInt(response.meta.total),
                totalPages: parseInt(response.meta.pages),
                hasNextPage: parseInt(response.meta.page) < parseInt(response.meta.pages),
                hasPreviousPage: parseInt(response.meta.page) > 1
            });

            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * PD inventory list for asset detail without mutating the global PD store.
     */
    async fetchListDetached(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [], meta: { total: 0 }, uniqueValues: {} };
        }
        try {
            const response = await this.service.getAll({
                page: params.page ?? 1,
                pageSize: params.pageSize ?? params.limit ?? 10,
                limit: params.limit ?? params.pageSize ?? 10,
                search: params.search ?? '',
                sortBy: params.sortBy ?? 'createdAt',
                sortOrder: params.sortOrder ?? params.sortDirection ?? 'desc',
                tenantId,
                ...(params.assetId ? { assetId: params.assetId } : {}),
                ...(params.excludeIds != null && params.excludeIds !== ''
                    ? { excludeIds: params.excludeIds }
                    : {}),
                ...Object.entries(params).reduce((acc, [key, value]) => {
                    if (key.startsWith('filter_')) acc[key] = value;
                    return acc;
                }, {}),
            });
            const meta = response.meta || {};
            return {
                success: true,
                data: response.data || [],
                meta: {
                    total: meta.total ?? 0,
                    page: meta.page ?? 1,
                    pages: meta.pages ?? 0,
                },
                uniqueValues: response.uniqueValues || {},
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                meta: { total: 0 },
                uniqueValues: {},
                error: this._getErrorMessage(error),
            };
        }
    }

    async getPdInventory(id) {
        const store = usePdInventoryStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            store.setSelectedItem(response.data);
            return response;
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Load a single record without mutating the global PD store (e.g. asset detail link flow).
     */
    async getPdInventoryDetached(id, opts = {}) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            if (!opts.skipErrorToast) {
                toast.error(errorMessage);
            }
            return { success: false, error: errorMessage };
        }
    }

    async createPdInventory(data, opts = {}) {
        const store = usePdInventoryStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (!opts.skipStoreLoading) store.setLoading(true);

        try {
            const response = await this.service.create({
                ...data,
                tenantId
            });

            if (!opts.skipSuccessToast) {
                toast.success(i18next.t('assetInventoryPdInventory:messages.createSuccess'));
            }

            if (!opts.skipListRefresh) {
                await this.fetchPdInventoryList();
            }
            if (!opts.skipCloseForm) {
                store.closeForm();
            } else if (response.data && !opts.skipOpenForm) {
                store.openForm(response.data);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            if (!opts.skipStoreLoading) store.setLoading(false);
        }
    }

    async updatePdInventory(id, data, opts = {}) {
        const store = usePdInventoryStore.getState();
        if (!opts.skipStoreLoading) store.setLoading(true);

        try {
            const response = await this.service.update(id, data);

            if (!opts.skipSuccessToast) {
                toast.success(i18next.t('assetInventoryPdInventory:messages.updateSuccess'));
            }

            if (!opts.skipListRefresh) {
                await this.fetchPdInventoryList();
            }
            if (!opts.skipCloseForm) {
                store.closeForm();
            }

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            if (!opts.skipStoreLoading) store.setLoading(false);
        }
    }

    async deletePdInventory(id, opts = {}) {
        const store = usePdInventoryStore.getState();
        if (!opts.skipStoreLoading) store.setLoading(true);

        try {
            await this.service.delete(id);

            if (!opts.skipSuccessToast) {
                toast.success(i18next.t('assetInventoryPdInventory:messages.deleteSuccess'));
            }

            if (!opts.skipListRefresh) {
                await this.fetchPdInventoryList();
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            if (!opts.skipStoreLoading) store.setLoading(false);
        }
    }

    async bulkDeletePdInventory(ids) {
        const store = usePdInventoryStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.batchDelete(ids);

            toast.success(i18next.t('assetInventoryPdInventory:messages.bulkDeleteSuccess', { count: response.deletedCount }));

            await this.fetchPdInventoryList();

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchPdPartyTemplate(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: null };
        }
        try {
            const response = await this.service.getPartyTemplate(params);
            return {
                success: response.success !== false,
                data: response.data,
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, data: null, error: errorMessage };
        }
    }

    async fetchPdParty(inventoryId) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !inventoryId) {
            return { success: false, data: null, partyTemplate: null };
        }
        try {
            const response = await this.service.getParty(inventoryId);
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, data: null, partyTemplate: null, error: errorMessage };
        }
    }

    async syncPdParty(inventoryId, payload, options = {}) {
        const { silentSuccess = false } = options;
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !inventoryId) {
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const response = await this.service.syncParty(inventoryId, payload);
            if (!silentSuccess) {
                toast.success(
                    i18next.t('assetInventoryPdInventory:ownership.saveSuccess', {
                        defaultValue: 'Ownership and roles saved',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }
}
